import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';

import { DeleteIcon, ViewIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import { Callout, IconButton, Paragraph, Popover } from '@treams/treams-ui';

import { ConversationRevealDataPreview } from './ConversationRevealDataPreview';
import { ConversationRevealDeleteConfirm } from './ConversationRevealDeleteConfirm';

interface IProps {
	evaluationFormID: string;
}

export function ConversationRevealUploadInfo({ evaluationFormID }: IProps) {
	const { t, i18n } = useTranslation();
	const [previewing, setPreviewing] = useState(false);
	const [confirmingDelete, setConfirmingDelete] = useState(false);

	const { data } = graphql.useEvaluationFormRevealUploadQuery({
		variables: { id: evaluationFormID },
	});

	const [deleteUpload, { loading: deleting }] = graphql.useDeleteRevealUploadMutation({
		variables: { evaluationFormID },
		refetchQueries: [graphql.EvaluationFormRevealUploadDocument],
		awaitRefetchQueries: true,
	});

	const upload = data?.evaluationForm.revealUpload;

	if (!upload) {
		return null;
	}

	const handleDismissDelete = () => {
		if (!deleting) {
			setConfirmingDelete(false);
		}
	};

	return (
		<Callout color="blue" mt={3}>
			<Flex width={1} justifyContent="space-between" style={{ gap: 8 }}>
				<Box>
					<Paragraph mb={2}>
						{t('conversations.reveal.settings.uploadInfo', {
							matchedCount: upload.matchedCount,
							date: upload.uploadedAt
								? new Date(upload.uploadedAt).toLocaleDateString(i18n.language)
								: '',
						})}
					</Paragraph>
					<Paragraph fontWeight="bold">{upload.selectedFields.join(', ')}</Paragraph>
				</Box>
				<Flex flexShrink={0} alignItems="flex-start" style={{ gap: 8 }}>
					<Popover.Target
						backdrop
						center
						visible={previewing}
						onDismiss={() => setPreviewing(false)}
						contents={
							previewing ? (
								<ConversationRevealDataPreview
									evaluationFormID={evaluationFormID}
									onClose={() => setPreviewing(false)}
								/>
							) : null
						}
					>
						<IconButton
							small
							type="plain"
							icon={<ViewIcon />}
							onClick={() => setPreviewing(true)}
						/>
					</Popover.Target>
					<Popover.Target
						backdrop
						center
						visible={confirmingDelete}
						onDismiss={handleDismissDelete}
						contents={
							confirmingDelete ? (
								<ConversationRevealDeleteConfirm
									deleting={deleting}
									onConfirm={() => deleteUpload()}
									onClose={() => setConfirmingDelete(false)}
								/>
							) : null
						}
					>
						<IconButton
							small
							type="plain"
							icon={<DeleteIcon />}
							onClick={() => setConfirmingDelete(true)}
						/>
					</Popover.Target>
				</Flex>
			</Flex>
		</Callout>
	);
}
